import React, { useState } from 'react';
import { IWebhookCatalogResponse, IWebhookCreateRequest, IWebhookEndpoint, IWebhookFilters } from '../../../../networked-shared-interfaces/community-admin/external-vendor-webhooks';
import { toastService } from '../../../../services/toast.service';
import { toggle } from './webhookHelpers';

interface IProps {
	endpoint?: IWebhookEndpoint | null;
	catalog: IWebhookCatalogResponse;
	isSaving?: boolean;
	onVerify: (url: string) => Promise<{ verified: boolean; message?: string; error?: string } | undefined>;
	onSubmit: (payload: IWebhookCreateRequest) => void;
	onCancel: () => void;
}

const WebhookEndpointForm = ({ endpoint, catalog, isSaving, onVerify, onSubmit, onCancel }: IProps) => {
	const spaces = [...(catalog.spaces ?? [])];
	const [name, setName] = useState(endpoint?.name ?? '');
	const [url, setUrl] = useState(endpoint?.url ?? '');
	// An existing endpoint is already verified for its saved URL.
	const [verifiedUrl, setVerifiedUrl] = useState(endpoint?.url ?? '');
	const [verifying, setVerifying] = useState(false);
	const [groups, setGroups] = useState<number[]>(endpoint?.subscribedGroups ?? []);
	const [events, setEvents] = useState<string[]>(endpoint?.subscribedEvents ?? []);
	const [openGroups, setOpenGroups] = useState<number[]>([]);
	const [spaceIds, setSpaceIds] = useState<string[]>(endpoint?.filters?.spaceIds ?? []);
	const [itemTypes, setItemTypes] = useState<string[]>(endpoint?.filters?.itemTypes ?? []);
	const [enabled, setEnabled] = useState(endpoint ? endpoint.status === 'active' : true);

	const isVerified = !!url.trim() && verifiedUrl === url.trim();
	const noneSelected = groups.length === 0 && events.length === 0;
	// Saved ids no longer in the list (deleted space) stay ticked, labelled by raw id.
	const spaceRows = [...spaces, ...spaceIds.filter((id) => !spaces.some((s) => s.id === id)).map((id) => ({ id, name: id }))];
	const typeGroups = catalog.groups.filter((g) => (catalog.itemTypes ?? []).some((it) => it.group === g.key));

	const toggleGroup = (g: (typeof catalog.groups)[number]) => {
		const inGroup = catalog.events.filter((e) => e.group === g.key).map((e) => e.name as string);
		const next = toggle(groups, g.code);
		setGroups(next);
		// Picking the whole group makes single picks inside it redundant.
		if (next.includes(g.code)) setEvents((cur) => cur.filter((e) => !inGroup.includes(e)));
	};

	const verify = async () => {
		const target = url.trim();
		setVerifying(true);
		const res = await onVerify(target);
		setVerifying(false);
		if (!res) return;
		if (res.verified) {
			setVerifiedUrl(target);
			toastService.success(res.message || 'Webhook URL verified successfully');
		} else {
			toastService.error(res.error || res.message || 'Webhook verification failed');
		}
	};

	const submit = (ev: React.FormEvent) => {
		ev.preventDefault();
		const filters: IWebhookFilters = {};
		if (spaceIds.length) filters.spaceIds = spaceIds;
		if (itemTypes.length) filters.itemTypes = itemTypes;
		onSubmit({ name: name.trim(), url: url.trim(), subscribedGroups: groups, subscribedEvents: events, filters, ...(endpoint && { enabled }) } as IWebhookCreateRequest);
	};

	return (
		<div className="card">
			<div className="card-body">
				<h5>{endpoint ? 'Edit webhook endpoint' : 'Add webhook endpoint'}</h5>
				<form onSubmit={submit}>
					<div className="form-group">
						<label htmlFor="wh-name"><strong>Name*</strong></label>
						<input id="wh-name" className="form-control" value={name} maxLength={100} required onChange={(e) => setName(e.target.value)} />
					</div>

					<div className="form-group">
						<label htmlFor="wh-url"><strong>Webhook URL*</strong></label>
						<div className="input-group">
							<input id="wh-url" type="url" className={`form-control ${isVerified ? 'is-valid' : ''}`} value={url} required
								placeholder="https://your-domain.com/webhook" onChange={(e) => setUrl(e.target.value)} />
							<div className="input-group-append">
								<button type="button" className={`btn ${isVerified ? 'btn-success' : 'btn-outline-primary'}`} onClick={verify} disabled={verifying || !url.trim()}>
									{verifying ? 'Verifying...' : isVerified ? 'Verified' : 'Verify URL'}
								</button>
							</div>
						</div>
						<small className="form-text text-muted">The URL must be verified before you can save.</small>
					</div>

					{endpoint && (
						<div className="form-group form-check">
							<input id="wh-enabled" type="checkbox" className="form-check-input" checked={enabled} onChange={() => setEnabled((v) => !v)} />
							<label htmlFor="wh-enabled" className="form-check-label">Enabled</label>
						</div>
					)}

					<div className="form-group">
						<label><strong>Events</strong></label>
						{noneSelected && <small className="form-text text-warning mb-2">No events selected: this endpoint will not receive anything until you choose events or groups.</small>}
						{catalog.groups.map((g) => {
							const groupEvents = catalog.events.filter((e) => e.group === g.key);
							const groupOn = groups.includes(g.code);
							const open = openGroups.includes(g.code);
							return (
								<div key={g.code} className="border rounded mb-1 px-2 py-1">
									<div className="d-flex align-items-center">
										<input type="checkbox" className="mr-2" checked={groupOn} onChange={() => toggleGroup(g)}
											aria-label={`${g.label}: all events in this group, including future ones`} />
										<span title={g.description}>{g.label}</span>
										<span className="badge badge-light ml-2">{groupEvents.length}</span>
										<button type="button" className="btn btn-link btn-sm ml-auto" aria-expanded={open} onClick={() => setOpenGroups((cur) => toggle(cur, g.code))}>
											{open ? 'Hide events' : 'Show events'}
										</button>
									</div>
									<small className="text-muted">All events in this group, including future ones</small>
									{open && groupEvents.map((e) => (
										<div key={e.name} className="form-check ml-3" title={String(e.code)}>
											<label className="form-check-label">
												<input type="checkbox" className="form-check-input" checked={groupOn || events.includes(e.name)} disabled={groupOn}
													onChange={() => setEvents((cur) => toggle(cur, e.name as string))} />
												{e.label} <small className="text-muted">{e.description}</small>
											</label>
										</div>
									))}
								</div>
							);
						})}
					</div>

					<div className="form-group">
						<label><strong>Filters</strong> <small className="text-muted">(optional)</small></label>
						<small className="form-text text-muted">Spaces: none selected = events from every space and community-level events</small>
						<div role="group" aria-label="Spaces" className="mb-2">
							{spaceRows.length === 0 && <small className="text-muted">This community has no spaces.</small>}
							{spaceRows.map((sp) => (
								<div key={sp.id} className="form-check form-check-inline">
									<label className="form-check-label">
										<input type="checkbox" className="form-check-input" checked={spaceIds.includes(sp.id)} onChange={() => setSpaceIds((cur) => toggle(cur, sp.id))} />
										{sp.name}
									</label>
								</div>
							))}
						</div>
						<small className="form-text text-muted">Item types: none selected = all item types</small>
						{typeGroups.map((g) => (
							<div key={g.key} role="group" aria-label={g.label}>
								<small className="text-muted mr-2">{g.label}</small>
								{(catalog.itemTypes ?? []).filter((it) => it.group === g.key).map((it) => (
									<div key={`${g.key}:${it.value}`} className="form-check form-check-inline">
										<label className="form-check-label">
											<input type="checkbox" className="form-check-input" checked={itemTypes.includes(it.value)} onChange={() => setItemTypes((cur) => toggle(cur, it.value))} />
											{it.label}
										</label>
									</div>
								))}
							</div>
						))}
					</div>

					{catalog.tokensPerDelivery > 0 && <small className="form-text text-muted mb-2">Each delivered webhook uses {catalog.tokensPerDelivery} token(s).</small>}

					<button type="submit" className="btn btn-success" disabled={!isVerified || !name.trim() || isSaving}>
						{isSaving ? 'Saving...' : endpoint ? 'Save changes' : 'Create endpoint'}
					</button>
					<button type="button" className="btn btn-secondary ml-2" onClick={onCancel} disabled={isSaving}>Cancel</button>
				</form>
			</div>
		</div>
	);
};

export default WebhookEndpointForm;
